import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

import { unwrapApiRecord } from "../shared/utils/apiResponse";
import { useUserContext } from "../userDashboard/contexts/userContext";
import { handleGetCurrentLocation, handleUpdateUserLocation } from "../userDashboard/userControllers/profileController";
import { userSilentApi as silentApi } from "../utils/apiInstances";
import {
    defaultLocation,
    getStoredLocation,
    saveLocation,
    toIso3,
    normalizeLocationPayload,
    getCountryNameFromIso3,
    LocationData,
    resetLocationData
} from "../utils/locationStorage";

const PREFERENCE_KEY = "saca_user_location_preference";
const PREFERENCE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const PREFERENCE_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour

interface LocationPreference {
    countryCode: string;
    countryName: string;
    state: string;
    city: string;
    timestamp: number;
    source?: string;
}

interface CountryDetailsShape {
    iso3?: string;
    iso2?: string;
    name?: string;
    iso_3?: string;
    iso_2?: string;
    code?: string;
}

interface LocationDetectionPayload extends Record<string, unknown> {
    country_code?: string;
    countryCode?: string;
    country?: string;
    state?: string;
    city?: string;
    country_details?: CountryDetailsShape;
}

const readLocationPreference = (): LocationPreference | null => {
    if (typeof globalThis === "undefined") {
        return null;
    }

    try {
        const raw = globalThis.localStorage.getItem(PREFERENCE_KEY);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch (error) {
        console.warn("Failed to parse stored location preference", error);
        return null;
    }
};

const preferenceAge = (preference: LocationPreference | null): number => {
    if (!preference?.timestamp) {
        return Number.POSITIVE_INFINITY;
    }

    return Date.now() - Number(preference.timestamp);
};

const isPreferenceFresh = (preference: LocationPreference | null, ttl = PREFERENCE_TTL_MS) => preferenceAge(preference) < ttl;

interface LocationContextType {
    location: LocationData;
    updateLocation: (nextLocation: Partial<LocationData>) => Promise<void>;
    resetToDefault: () => void;
    hasExplicitSelection: boolean;
}

const LocationContext = createContext<LocationContextType>({
    location: defaultLocation,
    updateLocation: async () => { },
    resetToDefault: () => { },
    hasExplicitSelection: false,
});

export const useLocationContext = () => useContext(LocationContext);

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { userProfile, loadingProfile, refreshUserProfile } = useUserContext?.() || {};
    const [location, setLocation] = useState<LocationData>(() => getStoredLocation());
    const [locationStabilized, setLocationStabilized] = useState(false);
    const [bootstrappedFromApi, setBootstrappedFromApi] = useState(false);

    const [hasExplicitSelection, setHasExplicitSelection] = useState(() => {
        const stored = getStoredLocation();
        const preference = readLocationPreference();

        if (isPreferenceFresh(preference)) {
            return true;
        }

        return (
            stored.countryCode !== defaultLocation.countryCode ||
            stored.state !== defaultLocation.state ||
            stored.city !== defaultLocation.city
        );
    });

    // Priority system for location sources (highest to lowest):
    // 1. User's manual selection (hasExplicitSelection = true)
    // 2. Authenticated user's profile country
    // 3. Previously stored location in localStorage
    // 4. Browser/Server detection (only if no other source exists)

    if (typeof globalThis !== "undefined" && process.env.NODE_ENV === "development") {
        (globalThis as { resetLocationData?: typeof resetLocationData }).resetLocationData = resetLocationData;
    }

    useEffect(() => {
        saveLocation(location);
    }, [location]);

    useEffect(() => {
        if (loadingProfile) {
            return;
        }

        if (!userProfile || Object.keys(userProfile || {}).length === 0) {
            return;
        }

        const profileCountry = toIso3(
            userProfile.country_code ||
            userProfile?.country?.iso3 ||
            userProfile?.country?.iso_3 ||
            userProfile?.country?.code ||
            userProfile?.country?.iso2 ||
            userProfile?.country?.iso_2 ||
            ""
        );

        if (!profileCountry) {
            return;
        }

        const profileCountryName =
            userProfile?.country?.name ||
            getCountryNameFromIso3(profileCountry) ||
            "";

        setLocation((prev) => {
            const normalized = normalizeLocationPayload({
                ...prev,
                countryCode: profileCountry,
                countryName: profileCountryName || prev.countryName,
                state: userProfile.state || "",
                city: userProfile.city || "",
            });

            if (
                normalized.countryCode === prev.countryCode &&
                normalized.state === prev.state &&
                normalized.city === prev.city
            ) {
                return prev;
            }

            return normalized;
        });

        setHasExplicitSelection(true);
        setLocationStabilized(true);

        if (typeof globalThis !== "undefined") {
            globalThis.localStorage.setItem(
                PREFERENCE_KEY,
                JSON.stringify({
                    countryCode: profileCountry,
                    countryName: profileCountryName,
                    state: userProfile.state || "",
                    city: userProfile.city || "",
                    timestamp: Date.now(),
                    source: "profile",
                })
            );
        }
    }, [loadingProfile, userProfile]);

    useEffect(() => {
        if (bootstrappedFromApi || loadingProfile) {
            return;
        }

        const token = typeof globalThis !== "undefined" ? localStorage.getItem("saca_token") : null;
        if (!token) {
            setBootstrappedFromApi(true);
            return;
        }

        const profileCountry = toIso3(
            userProfile?.country_code ||
            userProfile?.country?.iso3 ||
            userProfile?.country?.iso_3 ||
            userProfile?.country?.code ||
            userProfile?.country?.iso2 ||
            userProfile?.country?.iso_2 ||
            ""
        );

        if (profileCountry) {
            setBootstrappedFromApi(true);
            return;
        }

        (async () => {
            try {
                const result = await handleGetCurrentLocation();
                const payload = unwrapApiRecord<LocationDetectionPayload>(result);
                const detectedCountry = toIso3(
                    payload.country_code ||
                    payload.countryCode ||
                    payload?.country_details?.iso3 ||
                    payload?.country_details?.iso2 ||
                    ""
                );

                if (!detectedCountry) {
                    return;
                }

                const detectedName =
                    payload?.country_details?.name ||
                    payload.country ||
                    getCountryNameFromIso3(detectedCountry) ||
                    "";

                setLocation((prev) =>
                    normalizeLocationPayload({
                        ...prev,
                        countryCode: detectedCountry,
                        countryName: detectedName || prev.countryName,
                        state: payload.state || "",
                        city: payload.city || "",
                    })
                );

                setHasExplicitSelection(true);

                if (typeof globalThis !== "undefined") {
                    globalThis.localStorage.setItem(
                        PREFERENCE_KEY,
                        JSON.stringify({
                            countryCode: detectedCountry,
                            countryName: detectedName,
                            state: payload.state || "",
                            city: payload.city || "",
                            timestamp: Date.now(),
                            source: "bootstrap",
                        })
                    );
                }
            } catch (error) {
                console.warn("Failed to bootstrap location from API:", error);
            } finally {
                setBootstrappedFromApi(true);
            }
        })();
    }, [bootstrappedFromApi, loadingProfile, userProfile]);

    useEffect(() => {
        if (locationStabilized || loadingProfile) return;

        const stabilizeLocation = async () => {
            const isAuthenticated = !!localStorage.getItem("saca_token");
            const currentLocation = getStoredLocation();

            const preference = readLocationPreference();
            if (isPreferenceFresh(preference)) {
                setLocationStabilized(true);
                return;
            }

            if (hasExplicitSelection) {
                setLocationStabilized(true);
                return;
            }

            if (isAuthenticated && userProfile && Object.keys(userProfile || {}).length > 0) {
                const profileCountry = toIso3(
                    userProfile.country_code ||
                    userProfile?.country?.iso3 ||
                    userProfile?.country?.iso_3 ||
                    userProfile?.country?.code ||
                    userProfile?.country?.iso2 ||
                    userProfile?.country?.iso_2 ||
                    ""
                );

                if (profileCountry && profileCountry !== currentLocation.countryCode) {
                    setLocation((prev) => ({
                        ...prev,
                        countryCode: profileCountry,
                        countryName:
                            userProfile?.country?.name ||
                            getCountryNameFromIso3(profileCountry) ||
                            prev.countryName,
                        state: (userProfile.state as string) || "",
                        city: (userProfile.city as string) || "",
                    }));
                    setHasExplicitSelection(true);
                }
                setLocationStabilized(true);
                return;
            }

            if (currentLocation.countryCode && currentLocation.countryCode !== defaultLocation.countryCode) {
                setLocationStabilized(true);
                return;
            }

            try {
                const res = await silentApi("GET", "/user/location/detect");
                const detectPayload = unwrapApiRecord<LocationDetectionPayload>(res);
                const details = detectPayload.country_details || {};
                const detected = toIso3(details.iso3 || details.iso2 || "");
                const detectedName = details.name || getCountryNameFromIso3(detected) || "";

                if (detected && detected !== currentLocation.countryCode) {
                    setLocation((prev) => ({
                        ...prev,
                        countryCode: detected,
                        countryName: detectedName,
                        state: "",
                        city: "",
                    }));
                }
            } catch (error) {
                console.warn("Location detection failed, using default location");
            }

            setLocationStabilized(true);
        };

        stabilizeLocation();
    }, [hasExplicitSelection, loadingProfile, userProfile, locationStabilized]);

    useEffect(() => {
        if (!locationStabilized || loadingProfile) return;

        const isAuthenticated = !!localStorage.getItem("saca_token");
        if (isAuthenticated && userProfile && Object.keys(userProfile || {}).length > 0) {
            const profileCountry = toIso3(
                (userProfile.country_code as string) ||
                userProfile?.country?.iso3 ||
                userProfile?.country?.iso_3 ||
                userProfile?.country?.code ||
                userProfile?.country?.iso2 ||
                userProfile?.country?.iso_2 ||
                ""
            );

            if (profileCountry && profileCountry !== location.countryCode) {
                const preference = readLocationPreference();
                const recentOverride = isPreferenceFresh(preference, PREFERENCE_COOLDOWN_MS);

                if (!recentOverride) {
                    setLocation((prev) => ({
                        ...prev,
                        countryCode: profileCountry,
                        countryName:
                            userProfile?.country?.name ||
                            getCountryNameFromIso3(profileCountry) ||
                            prev.countryName,
                        state: (userProfile.state as string) || "",
                        city: (userProfile.city as string) || "",
                    }));
                }
            }
        }
    }, [locationStabilized, loadingProfile, userProfile, location.countryCode]);

    useEffect(() => {
        if (!location.countryCode) {
            return;
        }

        const normalized = toIso3(location.countryCode);
        const resolvedName = location.countryName || getCountryNameFromIso3(normalized);

        if (normalized && (normalized !== location.countryCode || !location.countryName)) {
            setLocation((prev) => ({
                ...prev,
                countryCode: normalized,
                countryName: resolvedName,
            }));
        }
    }, [location.countryCode, location.countryName]);

    const updateLocation = useCallback(
        async (nextLocation: Partial<LocationData>) => {
            const sanitized = { ...(nextLocation || {}) };

            if (typeof sanitized.countryCode === "string" && sanitized.countryCode.trim() === "") {
                delete sanitized.countryCode;
            }

            if (typeof sanitized.countryName === "string" && sanitized.countryName.trim() === "") {
                delete sanitized.countryName;
            }

            setHasExplicitSelection(true);
            setLocationStabilized(true);

            setLocation((prev) => {
                const normalized = normalizeLocationPayload({
                    ...prev,
                    ...sanitized,
                });
                return normalized;
            });

            const isAdminContext =
                typeof globalThis !== "undefined" &&
                (globalThis.location.pathname.startsWith("/admin-login") ||

                    Boolean(globalThis.localStorage?.getItem("saca_admin_token")));

            const shouldPersistToProfile = !isAdminContext;
            const hasToken =
                shouldPersistToProfile &&
                typeof globalThis !== "undefined" &&
                !!globalThis.localStorage?.getItem("saca_token");
            if (hasToken && sanitized.countryCode) {
                try {
                    await handleUpdateUserLocation(sanitized.countryCode);

                    // Save preference BEFORE refreshing profile to avoid race condition where
                    // profile refresh triggers a re-render before preference is saved,
                    // potentially causing a revert to the old profile location.
                    globalThis.localStorage?.setItem(
                        PREFERENCE_KEY,
                        JSON.stringify({
                            countryCode: sanitized.countryCode,
                            countryName: sanitized.countryName,
                            timestamp: Date.now(),
                        })
                    );

                    if (typeof refreshUserProfile === "function") {
                        await refreshUserProfile();
                    }
                } catch (error) {
                    console.warn("Failed to update user profile with new location:", error);
                }
            }

            setTimeout(() => {
                setLocation((prev) => ({ ...prev }));
            }, 0);
        },
        [refreshUserProfile]
    );

    const resetToDefault = useCallback(() => {
        setHasExplicitSelection(false);
        setLocation({ ...defaultLocation });
        if (typeof globalThis !== "undefined") {
            globalThis.localStorage.removeItem(PREFERENCE_KEY);
        }
    }, []);

    const value = useMemo(
        () => ({
            location,
            updateLocation,
            resetToDefault,
            hasExplicitSelection,
        }),
        [location, hasExplicitSelection, updateLocation, resetToDefault]
    );

    return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
};
